/**
 * @file HeaderBlock.tsx
 * Header of driver dispatch view
 *
 * components:
 *  HeaderBlock
 */

import React from "react";
import moment from "moment-timezone";
import first from "lodash/first";

import { Box, Divider, Typography } from "@mui/material";
import IconInfoSegment from "design-system/components/IconInfoSegment";
import {
  ClockIcon,
  EstimationIcon,
  TripIcon,
  UserIcon,
} from "design-system/icons";
import { grayDark, white } from "design-system/colors";
import { convertMinutesToHoursMinutes } from "utils/helpers";
import { OrderTypeRequirementEnum, Trip } from "types";
import { useOperator, useScreenSize, useTimeFormat } from "globals/hooks";
import { orderTypeEnumToNameMap } from "utils/enumMaps";
import TripStatus from "./TripStatus";
import FlightChangeDisplay from "components/globals/FlightChangeDisplay";

type HeaderBlockProps = {
  trip: Trip;
  orderType?: string;
  statusSlug: string;
};

function HeaderBlock(props: HeaderBlockProps) {
  const { trip, orderType, statusSlug } = props;

  // hooks
  const { isMobileView } = useScreenSize();
  const { isElSegundoConnect, operator } = useOperator();
  const { formatTime, formatDate } = useTimeFormat();
  const orderTypeRequirement = operator?.settings?.orderTypeRequirement;
  const showOrderType =
    orderTypeRequirement === OrderTypeRequirementEnum.Required ||
    (orderTypeRequirement === OrderTypeRequirementEnum.Optional && !!orderType);

  const pickUpStop = first(trip.stops);
  const pickUpOriginalTime = formatTime(
    moment.utc(pickUpStop?.originalDateTime)
  );
  const pickUpDate = formatDate(moment.utc(pickUpStop?.dateTime));
  const pickUpTime = formatTime(moment.utc(pickUpStop?.dateTime));
  const isDateTimeSame =
    moment
      .utc(pickUpStop?.originalDateTime)
      .diff(moment.utc(pickUpStop?.dateTime), "minutes") === 0;

  return (
    <Box bgcolor={white} pb={3} pt={1} px={2}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" flexDirection="row">
          <Box mr={2}>
            <Typography variant="overline" style={{ color: grayDark }}>
              Trip Date
            </Typography>
            <Typography variant="body1" style={{ fontWeight: "bold" }}>
              {pickUpDate}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" style={{ color: grayDark }}>
              Time
            </Typography>
            <Box>
              {pickUpStop?.flightOffset && !isDateTimeSame ? (
                <>
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: "bold",
                      textDecorationLine: "line-through",
                      color: grayDark,
                    }}
                  >
                    {pickUpOriginalTime}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="body1"
                      style={{ fontWeight: "bold", marginRight: 5 }}
                    >
                      {pickUpTime}
                    </Typography>
                    <FlightChangeDisplay
                      flightOffset={pickUpStop?.flightOffset}
                      originalDateTime={pickUpStop?.originalDateTime}
                      iconSize="small"
                    />
                  </Box>
                </>
              ) : (
                <Typography variant="body1" style={{ fontWeight: "bold" }}>
                  {pickUpTime}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box>
          {!isElSegundoConnect && <TripStatus statusSlug={statusSlug} />}
        </Box>
      </Box>

      <Divider />

      <Box
        mt={2}
        display="flex"
        flexDirection="row"
        flexWrap={isMobileView ? "wrap" : "nowrap"}
      >
        <Box mr={3}>
          <IconInfoSegment
            label="Passenger Count"
            value={trip.totalGroupSize}
            icon={UserIcon}
          />
        </Box>
        {showOrderType && (
          <Box mr={3}>
            <IconInfoSegment
              label="Order Type"
              value={orderTypeEnumToNameMap[orderType]}
              icon={TripIcon}
            />
          </Box>
        )}
        <Box mr={3}>
          <IconInfoSegment
            label={
              trip.useTotalDuration ? "total duration" : "estimated duration"
            }
            value={
              trip.useTotalDuration ? (
                convertMinutesToHoursMinutes(trip.totalDuration)
              ) : (
                <Box display="flex" alignItems="center">
                  <Box mr={0.5}>
                    {convertMinutesToHoursMinutes(trip.estimatedDuration)}
                  </Box>
                  <EstimationIcon size="small" />
                </Box>
              )
            }
            icon={ClockIcon}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default HeaderBlock;
